/**
 * 工作区状态管理 Store
 *
 * 使用 zustand 管理工作区状态，包括工作区列表、当前工作区、加载状态等。
 * 
 * 架构说明:
 * - 所有工作区CRUD操作均使用HTTP REST API
 * - 使用 HttpOnly Cookie 认证，无需手动传递 token
 * - WebSocket仅用于WorkspacePage的聊天功能
 *
 * 安全警告:
 * - 建议在生产环境使用 HTTPS 加密传输
 */

import { create } from 'zustand';
import { workspaceApi } from '../services/workspaceApi';
import { sanitizeErrorMessage } from '../utils/validation';
import type { Workspace, WorkspaceCreate, WorkspaceUpdate } from '../types/workspace';

// 重新导出 Workspace 类型，供其他组件使用
export type { Workspace };

// Store 状态接口
interface WorkspaceState {
  // 状态
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  loading: boolean;
  error: string | null;

  // 操作方法
  fetchWorkspaces: () => Promise<void>;
  createWorkspace: (data: WorkspaceCreate) => Promise<Workspace>;
  getWorkspace: (workspaceId: number) => Promise<Workspace>;
  updateWorkspace: (workspaceId: number, data: WorkspaceUpdate) => Promise<Workspace>;
  deleteWorkspace: (workspaceId: number) => Promise<void>;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  clearError: () => void;
  cleanup: () => void;
}

// 创建工作区 Store
export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  // 初始状态
  workspaces: [],
  currentWorkspace: null,
  loading: false,
  error: null,

  // 获取工作区列表（使用 Cookie 认证）
  fetchWorkspaces: async () => {
    set({ loading: true, error: null });
    try {
      const workspaces = await workspaceApi.listWorkspaces();
      set({ workspaces: workspaces, loading: false });
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '获取工作区列表失败，请稍后重试');
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  // 创建工作区（使用 Cookie 认证）
  createWorkspace: async (data: WorkspaceCreate) => {
    set({ loading: true, error: null });
    try {
      // 验证输入数据
      if (!data.name || data.name.trim().length < 1 || data.name.trim().length > 100) {
        throw new Error('工作区名称长度必须在1-100字符之间');
      }
      if (data.total_chapters < 1 || data.total_chapters > 10000) {
        throw new Error('章节数量必须在1-10000之间');
      }
      if (data.words_per_chapter < 500 || data.words_per_chapter > 50000) {
        throw new Error('每章节字数必须在500-50000之间');
      }

      const newWorkspace = await workspaceApi.createWorkspace(undefined, data);

      set({
        workspaces: [...get().workspaces, newWorkspace],
        currentWorkspace: newWorkspace,
        loading: false,
      });

      return newWorkspace;
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '创建工作区失败，请稍后重试');
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  // 获取工作区详情（使用 Cookie 认证）
  getWorkspace: async (workspaceId: number) => {
    set({ loading: true, error: null });
    try {
      const workspace = await workspaceApi.getWorkspace(undefined, workspaceId);
      set({ currentWorkspace: workspace, loading: false });
      return workspace;
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '获取工作区详情失败，请稍后重试');
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  // 更新工作区（使用 Cookie 认证）
  updateWorkspace: async (workspaceId: number, data: WorkspaceUpdate) => {
    set({ loading: true, error: null });
    try {
      const updatedWorkspace = await workspaceApi.updateWorkspace(undefined, workspaceId, data);

      const currentState = get();
      const { workspaces, currentWorkspace } = currentState;

      set({
        workspaces: workspaces.map(ws => (ws.id === workspaceId ? updatedWorkspace : ws)),
        currentWorkspace: currentWorkspace?.id === workspaceId ? updatedWorkspace : currentWorkspace,
        loading: false,
      });

      return updatedWorkspace;
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '更新工作区失败，请稍后重试');
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  // 删除工作区（使用 Cookie 认证）
  deleteWorkspace: async (workspaceId: number) => {
    set({ loading: true, error: null });
    try {
      await workspaceApi.deleteWorkspace(undefined, workspaceId);

      const currentState = get();
      const { workspaces, currentWorkspace } = currentState;

      set({
        workspaces: workspaces.filter(ws => ws.id !== workspaceId),
        currentWorkspace: currentWorkspace?.id === workspaceId ? null : currentWorkspace,
        loading: false,
      });
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '删除工作区失败，请稍后重试');
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  // 设置当前工作区
  setCurrentWorkspace: (workspace: Workspace | null) => {
    set({ currentWorkspace: workspace });
  },

  // 清除错误信息
  clearError: () => {
    set({ error: null });
  },

  // 清理资源
  cleanup: () => {
    set({
      workspaces: [],
      currentWorkspace: null,
      loading: false,
      error: null,
    });
  },
}));

export default useWorkspaceStore;
